<template>
  <div class="mensagens-programadas-table">
    <data-table
      ref="dataTableRef"
      :config="tableConfig"
      :data="rowData"
      :refreshButton="refreshButtonConfig"
      :csvButton="csvButtonConfig"
      @data-loaded="handleDataLoaded"
      @data-error="handleDataError"
      @button-click="handleButtonClick">
    </data-table>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted } from 'vue';
import { useI18n } from 'vue-i18n';
import DataTable from '@/grid/DataTable.vue';
import { useMensagensProgramadas } from '@/composables/useMensagensProgramadas';
import { useFiltro } from '@/composables/useFiltro';
import { useConfirmAction } from '@/composables/useConfirmAction';
import { eventBus } from '@/eventBus';
import { log } from '@/utils/DebugLogger';

type Id = number | string;

interface DataTableInstance {
  loadData: () => void;
}

interface CellParams {
  data: Record<string, unknown>;
  value?: unknown;
}

interface ScheduledMessageRow {
  message_id?: Id;
  recipient?: string;
  content?: string;
  scheduled_time?: string;
  status?: string;
  created_at?: string;
  meta?: string | Record<string, unknown>;
  [key: string]: unknown;
}

interface ButtonClickPayload {
  value?: Id;
  data: ScheduledMessageRow;
  action: string;
}

const { t } = useI18n();

// Composables
const { fetchScheduledMessages, cancelScheduledMessage, startPolling, stopPolling } = useMensagensProgramadas();
const { initialized } = useFiltro();
const { confirmAction } = useConfirmAction();

// Data
const rowData = ref<ScheduledMessageRow[]>([]);
const dataTableRef = ref<DataTableInstance | null>(null);

// DataTable Configuration
const tableColumns = computed(() => [
  {
    data: 'recipient',
    title: t('Destinatário'),
    width: 150,
    resizable: true,
    sortable: true,
    dataType: 'text'
  },
  {
    data: 'content',
    title: t('Conteúdo'),
    width: 350,
    resizable: true,
    sortable: true,
    dataType: 'text',
    cellRenderer: (params: CellParams): string => {
      const value = params.value as string | undefined;
      if (!value) return '';
      if (value.length > 100) {
        return value.substring(0, 100) + '...';
      }
      return value;
    }
  },
  {
    data: 'scheduled_time',
    title: t('Data/Hora Agendada'),
    width: 180,
    resizable: true,
    sortable: true,
    renderType: 'datetime',
    dataType: 'date'
  },
  {
    data: 'status',
    title: t('Status'),
    width: 120,
    resizable: true,
    sortable: true,
    dataType: 'text',
    cellRenderer: (params: CellParams): string => {
      const statusMap: Record<string, { label: string; severity: string }> = {
        'pending': { label: t('Pendente'), severity: 'warning' },
        'processing': { label: t('Processando'), severity: 'info' },
        'sent': { label: t('Enviada'), severity: 'success' },
        'failed': { label: t('Falhou'), severity: 'danger' },
        'cancelled': { label: t('Cancelada'), severity: 'secondary' }
      };

      const key = String(params.value ?? '');
      const status = statusMap[key] || { label: key, severity: 'info' };
      return `<span class="p-badge p-badge-${status.severity}">${status.label}</span>`;
    }
  },
  {
    data: 'created_at',
    title: t('Criado em'),
    width: 180,
    resizable: true,
    sortable: true,
    renderType: 'datetime',
    dataType: 'date'
  },
  {
    data: 'meta',
    title: t('Meta Dados'),
    width: 150,
    resizable: true,
    sortable: false,
    dataType: 'text',
    cellRenderer: (params: CellParams): string => {
      if (!params.value) return '';
      try {
        const meta = typeof params.value === 'string'
          ? JSON.parse(params.value) as Record<string, unknown>
          : params.value as Record<string, unknown>;
        let metaHtml = '';
        for (const key in meta) {
          metaHtml += `<strong>${key}:</strong> ${String(meta[key])}<br>`;
        }
        return metaHtml;
      } catch (e) {
        return String(params.value);
      }
    }
  },
  {
    data: null,
    title: t('Editar'),
    width: 90,
    resizable: false,
    sortable: false,
    renderType: 'clickable_button',
    buttonText: t('Editar'),
    buttonIcon: 'pi pi-pencil',
    buttonClass: 'p-button-secondary p-button-sm',
    buttonAction: 'edit',
    buttonTestId: (rowData: ScheduledMessageRow): string =>
      `mensagens-programadas-edit-${rowData.message_id ?? ''}`,
    buttonCondition: (rowData: ScheduledMessageRow): boolean => {
      return rowData.status === 'pending';
    }
  },
  {
    data: null,
    title: t('Cancelar'),
    width: 100,
    resizable: false,
    sortable: false,
    renderType: 'clickable_button',
    buttonText: t('Cancelar'),
    buttonIcon: 'pi pi-times',
    buttonClass: 'p-button-danger p-button-sm',
    buttonAction: 'cancel',
    buttonTestId: (rowData: ScheduledMessageRow): string =>
      `mensagens-programadas-cancel-${rowData.message_id ?? ''}`,
    buttonCondition: (rowData: ScheduledMessageRow): boolean => {
      return rowData.status === 'pending';
    }
  }
]);

const tableConfig = computed(() => ({
  tableId: 'tabelaMensagensProgramadas',
  title: t('Mensagens Programadas'),
  filename: `mensagens_programadas_${new Date().toISOString().split('T')[0]}`,
  columns: tableColumns.value,
  heightMode: 'fill-viewport',
  pagination: true,
  pageLength: 100,
  rowSelection: 'single',
  rowTestId: (data: Record<string, unknown>): string => {
    const id = data?.message_id;
    return id == null ? '' : `mensagens-programadas-row-${String(id)}`;
  }
}));

const refreshButtonConfig = computed(() => ({
  show: true,
  text: t('Atualizar')
}));

const csvButtonConfig = computed(() => ({
  show: true,
  text: t('CSV')
}));

// Methods
const loadData = async (): Promise<void> => {
  const stack = new Error().stack;
  log('MensagensProgramadasTable', 'loadData', 'Loading scheduled messages', {
    callStack: stack?.split('\n').slice(1, 4).join('\n')
  });
  try {
    const data = await fetchScheduledMessages() as ScheduledMessageRow[];
    log('MensagensProgramadasTable', 'loadData', 'Loaded data', data);
    rowData.value = data;

    // Start or stop polling based on whether there are active items
    const hasActiveItems = data.some(item => item.status === 'pending' || item.status === 'processing');
    if (hasActiveItems) {
      startPolling();
    } else {
      stopPolling();
    }
  } catch (error) {
    log('MensagensProgramadasTable', 'loadData', 'Error loading data', error);
    const message = error instanceof Error ? error.message : String(error);
    eventBus.emit('globalMessage.error', t('Erro ao carregar mensagens agendadas') + ': ' + message);
  }
};

const handleDataLoaded = (data: unknown[]): void => {
  log('MensagensProgramadasTable', 'handleDataLoaded', 'Data loaded', data.length, 'rows');
  eventBus.emit('globalMessage.success', t(`Carregados`) + ` ${data.length} ` + t(`mensagens agendadas`));
};

const handleDataError = (error: { message?: string } | Error): void => {
  log('MensagensProgramadasTable', 'handleDataError', 'Error loading data', error);
  const message = error instanceof Error ? error.message : (error.message || '');
  eventBus.emit('globalMessage.error', t('Erro ao carregar mensagens agendadas: ') + message);
};

const handleButtonClick = async ({ value, data, action }: ButtonClickPayload): Promise<void> => {
  log('MensagensProgramadasTable', 'handleButtonClick', 'Button clicked', { value, data, action });

  if (action === 'edit') {
    const scheduleId = data.message_id;

    if (!scheduleId) {
      log('MensagensProgramadasTable', 'handleButtonClick', 'No message_id found in row data for edit', data);
      eventBus.emit('globalMessage.error', t('ID da mensagem não encontrado'));
      return;
    }

    log('MensagensProgramadasTable', 'handleButtonClick', 'Opening edit wizard for scheduled message', scheduleId);
    eventBus.emit('modal.mensagens-programadas-wizard.open', {
      editData: data
    });
    return;
  }

  if (action === 'cancel') {
    // Get message_id from the row data
    const scheduleId = data.message_id;

    if (!scheduleId) {
      log('MensagensProgramadasTable', 'handleButtonClick', 'No message_id found in row data', data);
      eventBus.emit('globalMessage.error', t('ID da mensagem não encontrado'));
      return;
    }

    // Show confirmation dialog
    confirmAction({
      message: t('Tem certeza que deseja cancelar esta mensagem agendada?'),
      header: t('Confirmação'),
      onAccept: async () => {
        try {
          log('MensagensProgramadasTable', 'handleButtonClick', 'Cancelling scheduled message', scheduleId);
          await cancelScheduledMessage(Number(scheduleId));
          eventBus.emit('globalMessage.success', t('Mensagem agendada cancelada com sucesso!'));
          loadData(); // Refresh the table
        } catch (error) {
          log('MensagensProgramadasTable', 'handleButtonClick', 'Error cancelling scheduled message', error);
          const message = error instanceof Error ? error.message : String(error);
          eventBus.emit('globalMessage.error', t('Erro ao cancelar mensagem agendada') + ': ' + message);
        }
      }
    });
  }
};

// Event Listeners
onMounted(() => {
  log('MensagensProgramadasTable', 'onMounted', 'Component mounted', {
    filterInitialized: initialized.value
  });

  // Don't load data on mount if filter is not initialized yet
  // The filter component will trigger load when ready
  if (initialized.value) {
    log('MensagensProgramadasTable', 'onMounted', 'Filter initialized, loading data');
    loadData();
  } else {
    log('MensagensProgramadasTable', 'onMounted', 'Waiting for filter initialization');
  }

  // Listen for filter updates
  eventBus.on('filter.updated', loadData);

  // Listen for refresh events
  eventBus.on('mensagens-programadas.refresh', loadData);
});

onUnmounted(() => {
  log('MensagensProgramadasTable', 'onUnmounted', 'Component unmounted');
  stopPolling();
  eventBus.off('filter.updated', loadData);
  eventBus.off('mensagens-programadas.refresh', loadData);
});

// Expose methods for parent components
defineExpose({
  loadData
});
</script>

<style scoped>
.mensagens-programadas-table {
  margin-top: 20px;
}
</style>
